import * as React from 'react';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { useCurrentUser } from '@docavenue/components';
import { asyncActions } from '@docavenue/core';
import { get, isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import { IconCenter, IconStar, IconStarFilled } from '@maiia/design-system';
import { analyticsEvent } from '@/src/analytic_tag_manager';

import Avatar from '@/components/atoms/Avatar/Avatar';

import Text from '@/components/atoms/Text/Text';
import Typography from '@/components/atoms/Typography/Typography';
import { getPictureSource } from '@/components/utils/card';
import { useTranslation } from '@/src/i18n';
import { getCardFullName } from '@/components/utils';
import IconSwitcher from '@/components/atoms/IconSwitcher/IconSwitcher';
import Dialog from '@/components/molecules/Dialog/Dialog';
import FavoriteSelector from './FavoriteSelector';
import {
  authActions,
  favoritePractitionersActions,
  usersActions,
} from '@/src/actions';
import { enqueueSnackbar } from '@/src/actions/snackbar';
import { pictures } from '@/src/utils';
import DialogSlider from '../DialogSlider/DialogSlider';
import OpeningStatus from './OpeningStatus';
import { APP_ROUTES, PHARMACY } from '@/src/constants';
import RDVButton from './RDVButton';

type Props = {
  card: CardHcd;
};

const CenterHeader = ({ card }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const account = useCurrentUser();
  const [openSlider, setOpenSlider] = React.useState(false);
  const patientsList = account?.userPatientInformation?.userPatientProfiles;
  const isClientDocavenue = card?.settings?.clientDocavenue;
  const [showFavoriteSelector, setShowFavoriteSelector] = React.useState(false);
  const dispatch = useDispatch();

  const {
    center: { id: centerId, practitioners = [], speciality } = {},
  } = card;

  const isFavorite = React.useMemo(
    () =>
      patientsList?.some(profile =>
        profile.favoriteCenters?.some(
          favoriteCenter => favoriteCenter.centerId === centerId,
        ),
      ),
    [account, centerId],
  );

  const [flagPrat, setFlagPrat] = React.useState<{
    icon: React.ReactNode;
    selected: boolean;
    style: string;
    isSelected?: React.ReactNode;
  }>({
    icon: <IconStar />,
    selected: false,
    style: 'favorite',
  });

  React.useEffect(() => {
    if (!isFavorite) {
      setFlagPrat({
        icon: <IconStar data-testid="is-not-favorite" />,
        isSelected: <IconStarFilled aria-label={t('in_favorites')} />,
        selected: false,
        style: 'favorite',
      });
    }

    if (isFavorite) {
      setFlagPrat({
        icon: <IconStar />,
        isSelected: (
          <IconStarFilled
            data-testid="is-favorite"
            aria-label={t('in_favorites')}
          />
        ),
        selected: true,
        style: 'favorite',
      });
    }
  }, [isFavorite]);

  const childCenters = (card.center?.childCenters ?? []).filter(
    c => c.isVisibleOnline,
  );

  const nSpecialities = (() => {
    const specialities = new Set(practitioners.map(p => p.speciality?.name));
    specialities.delete(undefined);
    return specialities.size;
  })();

  const isPharmacy = speciality?.shortName === 'pharmacie';

  const fullName = React.useMemo(() => getCardFullName(t, card), [card, t]);

  return (
    <div className="profile-header center-header">
      <div className="profile-header__prat">
        <Avatar
          type={isPharmacy ? PHARMACY : card?.type}
          size="big"
          source={getPictureSource(card)}
          picturesNbr={pictures(card).length}
          onClick={() => {
            if (pictures(card).length > 0) setOpenSlider(true);
            analyticsEvent({
              category: 'PractitionerOrCenterPage',
              action: 'ClickPicture',
              label:
                pictures(card).length > 0 ? 'hasPicture' : 'defaultPicture',
            });
          }}
          title={fullName}
          aria-label={fullName}
          data-testid="center-profile-picture"
        />

        <div className="name-n-spe">
          <Typography className="name" variant="h1" data-testid="center-name">
            <span>{fullName}</span>
            {isClientDocavenue && (
              <IconSwitcher
                className={flagPrat.style}
                on={flagPrat.selected}
                onIcon={flagPrat.isSelected}
                offIcon={flagPrat.icon}
                onClick={async () => {
                  if (isEmpty(account)) {
                    return router.push({
                      pathname: APP_ROUTES.LOGIN,
                      query: { next: card.url, fromPreferences: true },
                    });
                  }
                  if (
                    account?.userPatientInformation?.userPatientProfiles
                      ?.length === 1
                  ) {
                    await asyncActions(
                      dispatch,
                      favoritePractitionersActions.updateManyPost(
                        [
                          {
                            profileId:
                              account?.userPatientInformation
                                ?.userPatientProfiles?.[0].id,
                            type: isFavorite ? 'NONE' : 'FAVORITE',
                          },
                        ],
                        {
                          params: {
                            centerId,
                          },
                        },
                      ),
                    );
                    const user = await asyncActions(
                      dispatch,
                      usersActions.getOne('me'),
                    );
                    dispatch(
                      authActions.setItem({
                        ...account,
                        ...user,
                      }),
                    );
                    let message;
                    if (isFavorite) message = 'center_removed_from_care_team';
                    else message = 'center_added_to_care_team';
                    dispatch(
                      enqueueSnackbar({
                        message: t(message),
                        severity: 'success',
                        duration: 1500,
                      }),
                    );
                  } else {
                    setShowFavoriteSelector(true);
                  }
                  analyticsEvent({
                    category: 'PractitionerOrCenterPage',
                    action: 'ClickFavorite',
                  });
                }}
              />
            )}
          </Typography>
        </div>

        <Box display="flex" alignItems="center" className="center-count">
          {childCenters?.length > 0 && (
            <Text className="children-count">
              {t('centers_children', { count: childCenters?.length || 0 })}
            </Text>
          )}

          {!childCenters?.length && (
            <>
              {nSpecialities > 1 && (
                <Text className="spe-count">
                  {t('centerheader_specialities', { count: nSpecialities })}
                </Text>
              )}
              {practitioners.length > 1 && (
                <Text className="prat-count">
                  {t('centerheader_practitioners', {
                    count: practitioners.length,
                  })}
                </Text>
              )}
            </>
          )}
          {/* Specific mobile-tablet */}
          {speciality?.name && (
            <Text
              data-cy="entity-type"
              className="center-type hide-mobile"
              iconComponent={<IconCenter />}
            >
              {speciality.name}
            </Text>
          )}
          {isPharmacy && isClientDocavenue && (
            <span className="opening-hours hide-mobile">
              <OpeningStatus card={card} />
            </span>
          )}
        </Box>

        <div className="center-details hide-desktop">
          {speciality?.name && (
            <Text className="center-type" iconComponent={<IconCenter />}>
              {/* Make sure that speciality name will fit on maximum two lines on mobile (about 24 characters) */}
              {speciality.name?.length > 24
                ? `${speciality.name.substring(0, 24)}...`
                : speciality.name}
            </Text>
          )}
          {isPharmacy && isClientDocavenue && (
            <span className="opening-hours">
              <OpeningStatus card={card} />
            </span>
          )}
        </div>
        <RDVButton card={card} />
      </div>
      <Dialog
        open={showFavoriteSelector}
        onClose={() => setShowFavoriteSelector(false)}
        title={t(
          `favorite_${
            isPharmacy ? 'pharmacy' : 'doctor'
          }_profile_selector_title`,
          { fullName: getCardFullName(t, card) },
        )}
      >
        <FavoriteSelector
          account={account}
          practitionerId={get(card, 'professional.practitionerId')}
          centerId={get(card, 'center.id')}
          onClose={() => {
            setShowFavoriteSelector(false);
          }}
          card={card}
        />
      </Dialog>
      <DialogSlider open={openSlider} setOpen={setOpenSlider}>
        {pictures(card)?.map(picture => (
          <img
            key={picture?.s3Id}
            src={`/files/${picture?.s3Id}`}
            alt="images"
          />
        ))}
      </DialogSlider>
    </div>
  );
};

export default CenterHeader;
